import { useState, useRef, useEffect, useMemo } from 'react';
import { useDebouncedPastScrollThreshold } from 'hooks/useDebouncedWindowScroll';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  breakpoints,
  FILTER_MENU_Z_INDEX,
  layers,
  useWindowSize,
} from 'app/spacing';
import Icon from 'components/Kizen/Icon';
import BackButton from 'components/Button/BackButton';
import SelectOverlay from 'components/Inputs/SelectOverlay';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import useModal from 'components/Modals/useModal';
import MobileActivitySelector from 'pages/Common/MobileActivitySelector';
import MobileAutomationSelector from 'pages/Common/MobileAutomationSelector';
import { StyledSelectMenu } from '__pages/Common/StyledSelect';
import {
  ToolbarWrapper,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  SaveButton,
  DesktopContent,
  DesktopContactContent,
  DesktopOnlyButton,
  MobileContent,
  Header,
  NameWrapper,
  Name,
  ActionsWrapper,
  ThreeDots,
  FlyingDot,
  DesktopOnlyButtonWithDropdown,
} from 'pages/Common/styles/ControlBar';
import styled from '@emotion/styled';
import ArchiveModal from 'components/Modals/ArchiveModal';
import KizenTypography from 'app/kizentypo';
import ActionBar from './ActionBar';
import { Typography } from '@kizen/kds/Typography';
import { ViewSelect } from './styles';

const DEFAULT_NAME = (t) => t('Custom Object');
const NAME_BREAKPOINT = breakpoints.md;

const StyledToolbarWrapper = styled(ToolbarWrapper)`
  z-index: ${FILTER_MENU_Z_INDEX +
  1}; // high z-index to be above the filter dropdowns on the constrained timeline
`;

export const ControlBar = ({
  customObjectId,
  access = { edit: false, remove: false },
  recordName,
  recordDisplayName,
  onSubmit,
  onDelete,
  onStartAutomation,
  defaultOnActivities,
  layoutOptions,
  selectedLayout,
  setSelectedLayout,
  hideActions = false,
  canStartAutomations,
  performActionOptions,
  onChange,
  openPerformModal,
  actionsDisabled = false,
}) => {
  const { t } = useTranslation();
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [showSelectedAutomation, setShowSelectedAutomation] = useState();
  const [submitting, setSubmitting] = useState(false);

  const actionOptions = useMemo(
    () =>
      [
        {
          value: 'activity',
          label: t('Log Activity'),
          onClick: () => setShowLogActivity(true),
        },
        access.edit &&
          canStartAutomations && {
            value: 'automations',
            label: t('Start Agentic Workflow'),
            onClick: () => setShowSelectedAutomation(true),
          },
      ].filter(Boolean),
    [t, access.edit, canStartAutomations]
  );

  const { objectId } = useParams();
  const scrolled = useDebouncedPastScrollThreshold();
  const [tooltipProps, tooltip] = useTruncationTooltip();
  const { width: screenWidth } = useWindowSize();

  const [showActionMenu, setShowActionMenu] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const actionButtonRef = useRef(null);

  const [confirmDeletionModalProps, confirmDeletionModalTriggerProps] =
    useModal({
      handleSubmit: onDelete,
    });

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  const titleRef = useRef(null);

  useEffect(
    () => {
      if (!scrolled) {
        // makes sure the menus close if scrolled
        setShowActionMenu(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrolled]
  );

  return (
    <>
      <StyledToolbarWrapper className="toolbar-wrapper" scrolled={scrolled}>
        <Toolbar className="toolbar" scrolled={scrolled}>
          <ToolbarLeft nameBreakpoint={NAME_BREAKPOINT} style={{ gap: '15px' }}>
            <BackButton to={`/custom-objects/${objectId}`}>
              <DesktopContent as="span">{t('back')}</DesktopContent>
            </BackButton>
            {layoutOptions?.length > 1 ? (
              <div className="kds flex items-center gap-x-spacer-10 ml-[-20px]">
                <Typography>{t('View')}</Typography>
                <ViewSelect
                  options={layoutOptions}
                  onChange={setSelectedLayout}
                  value={selectedLayout}
                />
              </div>
            ) : null}
          </ToolbarLeft>
          <DesktopContactContent nameBreakpoint={NAME_BREAKPOINT}>
            <NameWrapper>
              <Name
                {...tooltipProps}
                type="header"
                data-qa="entity-display-name"
              >
                {recordDisplayName || recordName || DEFAULT_NAME(t)}
              </Name>
            </NameWrapper>
          </DesktopContactContent>
          <MobileContent>
            <Header ref={titleRef} {...tooltipProps} type="subheader">
              {recordDisplayName || recordName || DEFAULT_NAME(t)}
            </Header>
          </MobileContent>
          {!hideActions ? (
            <ToolbarRight
              nameBreakpoint={NAME_BREAKPOINT}
              style={{ alignItems: 'center', gap: '15px' }}
            >
              {access.remove && (
                <DesktopOnlyButton
                  {...confirmDeletionModalTriggerProps}
                  color="red"
                >
                  {t('Archive')}
                </DesktopOnlyButton>
              )}
              {performActionOptions?.length ? (
                <DesktopOnlyButtonWithDropdown
                  noSpace
                  color="blue"
                  onChange={onChange}
                  onClick={openPerformModal}
                  options={performActionOptions}
                  data-qa="perform-action-button"
                  disabled={actionsDisabled}
                >
                  {t('Perform Action')}
                </DesktopOnlyButtonWithDropdown>
              ) : null}
              {access.edit && (
                <DesktopOnlyButton
                  disabled={submitting}
                  onClick={handleSave}
                  color="green"
                >
                  {t('Save')}
                </DesktopOnlyButton>
              )}
              <MobileContent>
                {actionOptions.length > 0 ? (
                  <>
                    <ThreeDots
                      className={`three-dots ${scrolled ? 'is-scrolled' : ''}`}
                      screenWidth={screenWidth}
                      ref={actionButtonRef}
                      onClick={() => setShowActionMenu(true)}
                    >
                      <Icon icon="three-dot" />
                      {actionOptions.length > 1 ? (
                        <FlyingDot screenWidth={screenWidth} className="b" />
                      ) : null}
                      <FlyingDot screenWidth={screenWidth} className="c" />
                    </ThreeDots>
                    <SelectOverlay
                      show={showActionMenu}
                      target={titleRef.current}
                      onHide={() => {
                        setShowActionMenu(false);
                      }}
                      zIndexNudge={layers.content(0, 3)}
                    >
                      <StyledSelectMenu
                        value={selectedAction || null}
                        onChange={(value) => {
                          setSelectedAction(null);
                          setShowActionMenu(false);
                          value.onClick();
                        }}
                        options={actionOptions}
                        autoFocus={false}
                        placeholder={t('Select Option')}
                        menuOnly
                      />
                    </SelectOverlay>
                  </>
                ) : null}

                {defaultOnActivities ? (
                  <MobileActivitySelector
                    showLogActivity={showLogActivity}
                    target={titleRef.current}
                    customObjectId={customObjectId}
                    onSetVisible={(val) => setShowLogActivity(val)}
                    scrolled={scrolled}
                    shownScrolled
                  />
                ) : null}
                <MobileAutomationSelector
                  showSelectedAutomation={showSelectedAutomation}
                  target={titleRef.current}
                  onSetVisible={(val) => setShowSelectedAutomation(val)}
                  scrolled={scrolled}
                  shownScrolled
                  onStartAutomation={onStartAutomation}
                  customObjectId={objectId}
                />
              </MobileContent>

              <MobileContent>
                <SaveButton
                  variant="text"
                  disabled={submitting}
                  onClick={handleSave}
                >
                  {t('save')}
                </SaveButton>
              </MobileContent>
            </ToolbarRight>
          ) : (
            <ToolbarRight nameBreakpoint={NAME_BREAKPOINT} />
          )}
        </Toolbar>
      </StyledToolbarWrapper>
      <MobileContent>
        <ActionsWrapper className="ActionsWrapper">
          <ActionBar
            condensed={false}
            hasEditAccess={access.edit}
            scrolled={scrolled}
            onStartAutomation={onStartAutomation}
            defaultOnActivities={defaultOnActivities}
            customObjectId={objectId}
            canStartAutomations={canStartAutomations}
          />
        </ActionsWrapper>
      </MobileContent>
      <ArchiveModal {...confirmDeletionModalProps}>
        <KizenTypography>
          {t('This will archive the')}{' '}
          {recordDisplayName || recordName || DEFAULT_NAME(t)}{' '}
          {t(
            'record and all associated information, so that it can no longer be accessed.'
          )}
        </KizenTypography>
      </ArchiveModal>
      {tooltip}
    </>
  );
};

export default ControlBar;
